import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AppUpdatePopupComponent } from '../components/app-update-popup/app-update-popup.component';
import { AppUpdatesStore } from '../store/app-updates.store';
import { AppUpdate } from './app-updates.service';

/**
 * Loads popup announcements on app entry and shows them sequentially as modals.
 * Each one is recorded as seen on dismiss; the backend only returns it again
 * once its repeat-every-N-days window has passed (never, if it doesn't repeat).
 *
 * Triggered from the authenticated layout (drawer): once on entry, and again
 * whenever the user navigates to the home route (`/`). Because getPopups()
 * returns only the popups that are currently due (never seen, or past their
 * repeat window), a new announcement created while the user is already inside
 * the app surfaces the next time they land on `/`, and a seen one only shows
 * again once its repeat window has passed.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdatePopupService {
	private dialog = inject(MatDialog);
	private store = inject(AppUpdatesStore);
	private router = inject(Router);

	// Guards against overlapping runs: the entry call and a navigation to `/`
	// could otherwise open the same popup queue twice.
	private running = false;

	async showPendingPopups(): Promise<void> {
		// No session-level gate: getPopups() already returns only pending (unread)
		// announcements, and a `shown` flag on this root-scoped service would
		// persist across logout/login and suppress popups for the next user.
		if (this.running) return;
		this.running = true;
		try {
			await this.store.loadPopups();
			const popups = this.store.popups();

			for (const popup of popups) {
				await this.showOne(popup);
			}
		} finally {
			this.running = false;
		}
	}

	/**
	 * Opens an offer from the carousel / ribbon in the same modal as the popups.
	 * It does NOT record a popup view: that would restart its repeat-every-N-days
	 * window and skew the admin stats. It only marks it read in Novedades.
	 */
	async openOffer(offer: AppUpdate): Promise<void> {
		const openLink = await this.openDialog(offer);
		await this.store.markOfferAsRead(offer);
		if (openLink) this.followLink(offer.link);
	}

	private async showOne(popup: AppUpdate): Promise<void> {
		const openLink = await this.openDialog(popup);
		await this.store.dismissPopup(popup, openLink);
		if (openLink) this.followLink(popup.link);
	}

	/** Resolves true when the user pressed «Ver más». */
	private async openDialog(item: AppUpdate): Promise<boolean> {
		const ref = this.dialog.open(AppUpdatePopupComponent, {
			data: item,
			maxWidth: '95vw',
			width: '420px',
			panelClass: 'app-update-popup-panel',
			autoFocus: false,
		});
		const result = await lastValueFrom(ref.afterClosed());
		return result?.openLink === true;
	}

	private followLink(link?: string): void {
		if (!link) return;
		if (/^https?:\/\//i.test(link)) {
			window.open(link, '_blank', 'noopener');
		} else {
			this.router.navigateByUrl(link);
		}
	}
}
